import React, { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';

// A CONVERSATION AS A SCENE (2026-11-01).
//
// Khari: "We should make the chat UI in scene mode. Show lyra up close and
// overlay a customized chat HUD. we will use this same format for select chat
// scenes. We may extend this to all chats in world … show a 1st person POV with
// her facing us, up close. On desktop, she would be situated to the left with
// the text/chat log to the right. On mobile, the chat may need to shift centered
// and lowered, overlaying her lower area with a shadow behind it."
//
// A chat opens as a scene when whoever opens it says where the person's face is:
//   openChat({ …, scene: { roomId, face: [x, y, z], rotY, sub } })   a fixed point
//     in room space (the front desk, FrontDesk)
//   openChat({ …, scene: bodyScene(group, { avatarId, height }) })   the group their
//     body stands in, followed live — every artist in the world (2026-11-01, Khari:
//     "convert our in-world artist chats to scene mode (like Lyra's)"): on the
//     street (CityResident), at a room's marks (RoomPerson: guests and hosts), at a
//     café table (CafeGuests, sitting).
//
//   ChatSceneCamera (in the canvas)  takes the camera to them — up close, facing
//                                    you, framed for the layout below — and
//                                    eases back to exactly where you stood
//   AvatarChatDock / GuestChatDock   the SAME conversation, laid out over the
//                                    shot (sceneWrap + SceneHead) instead of the
//                                    corner panel, while the camera is held
//   useSceneChat                     `on` while the camera is held (the docks
//                                    switch layout on it; App hides the docks
//                                    that would sit over the shot); `speaking`
//                                    = whose voice is playing, so their face
//                                    moves (FrontDesk)
//
// WIDE (a desktop): they stand left of centre, the chat is a column on the right.
// NARROW (a phone, or any tall window): they are centred with their face high,
// and the chat sits low across them over a shadow that fades up into the shot.

export const useSceneChat = create(() => ({ on: false, speaking: null }));

/** A scene on someone's body: `anchor` is the group it stands in (+z = the way they face). */
export const bodyScene = (anchor, { avatarId, height = 0.75, seated = false, sub = null } = {}) => (
  anchor ? { anchor, avatarId: Number(avatarId) || null, eye: height * (seated ? 0.69 : 0.905), sub } : null
);

/** A phone or a tall window: the chat goes low and centred. */
export const isNarrow = (w, h) => w < 760 || w / Math.max(1, h) < 0.8;

export function useNarrow() {
  const read = () => (typeof window === 'undefined' ? false : isNarrow(window.innerWidth, window.innerHeight));
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    const on = () => setNarrow(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return narrow;
}

// The wide column — ChatSceneCamera frames the face in the middle of what it leaves.
export const COLUMN = { maxPx: 440, vw: 0.38, minRightPx: 20, rightVw: 0.035 };
/** px the column takes from the right edge of a `w`-wide window (its width plus its margin). */
export const columnPx = (w) => Math.min(COLUMN.maxPx, COLUMN.vw * w) + Math.max(COLUMN.minRightPx, COLUMN.rightVw * w);

export const sceneWrap = (narrow) => (narrow ? {
  position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 70,
  height: 'min(58dvh, 560px)', boxSizing: 'border-box', paddingTop: 64, paddingBottom: 'env(safe-area-inset-bottom)',
  display: 'flex', flexDirection: 'column', pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif',
  // The shadow: solid where the words are, fading up into the shot.
  background: 'linear-gradient(to top, rgba(8,5,3,0.95) 0%, rgba(8,5,3,0.88) 56%, rgba(8,5,3,0.5) 82%, rgba(8,5,3,0) 100%)',
} : {
  position: 'fixed', zIndex: 70,
  right: `max(${COLUMN.minRightPx}px, ${COLUMN.rightVw * 100}vw)`, top: 'max(64px, 9vh)', bottom: 'max(20px, 7vh)',
  width: `min(${COLUMN.maxPx}px, ${COLUMN.vw * 100}vw)`,
  display: 'flex', flexDirection: 'column', pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif',
  background: 'linear-gradient(180deg, rgba(12,8,5,0.6), rgba(12,8,5,0.84))',
  border: '1px solid rgba(255,178,94,0.22)', borderRadius: 20, overflow: 'hidden',
  backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
});

/** A roster name up to its tag ("Lyra - HypeTunes Assistant" → "Lyra"), and the tag. */
export function splitName(name) {
  const [first, ...rest] = String(name || '').split(/\s+[-–—|]\s+/);
  return { name: first || String(name || ''), tag: rest.join(' · ') || null };
}

export function SceneHead({ name, sub, onClose, narrow, accent = '#ffd27d' }) {
  const n = splitName(name);
  const line = n.tag || sub;   // their own tag ("HypeTunes Assistant"), else where they are
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: narrow ? '0 18px 10px' : '18px 20px 12px' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: accent, font: '800 22px system-ui', letterSpacing: 0.3, lineHeight: 1.1, textShadow: narrow ? '0 1px 8px rgba(0,0,0,0.6)' : 'none' }}>{n.name}</div>
        {line && <div style={{ color: 'rgba(255,233,196,0.62)', font: '700 10.5px system-ui', letterSpacing: 1.4, textTransform: 'uppercase', marginTop: 5 }}>{line}</div>}
      </div>
      <button type="button" onClick={onClose} aria-label="Leave the conversation" title="Leave (Esc)" style={{
        flex: 'none', cursor: 'pointer', padding: '7px 13px', borderRadius: 999, border: '1px solid rgba(255,178,94,0.4)',
        background: 'rgba(20,12,6,0.55)', color: '#ffe9c4', font: '700 12.5px system-ui',
      }}>✕ Leave</button>
    </div>
  );
}

/** Esc leaves the scene (the camera eases back as the chat closes). */
export function useEscToLeave(active, onLeave) {
  const fn = useRef(onLeave);
  fn.current = onLeave;
  useEffect(() => {
    if (!active) return undefined;
    const key = (e) => { if (e.key === 'Escape') fn.current(); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [active]);
}

/**
 * Their face moves while they speak to you, in any chat with someone in the
 * world (2026-11-01, Khari: "when sitting at a table talking with an artist,
 * their talking face should be activated if available. (same for the scene
 * chats)"): while their voice plays (the mic), and for about as long as a typed
 * reply takes to say — a typed reply has no audio, so the face reads it out
 * (AnimatedResident moves the chatted-to body on `speaking`). The history a chat
 * opens with is not said again: only a reply that lands a moment after it opened.
 */
const SAY_S_PER_WORD = 0.33;   // an unhurried read
const SAY_MIN_S = 1.6;
const SAY_MAX_S = 14;
export function useChatSpeaking(who, { voice = false, reply = null } = {}) {
  // An avatar id, or a key for a body with no id of its own (a counter's
  // `actor:<role key>`, InteriorResidents' StagedResident).
  const id = typeof who === 'string' ? who : (Number(who) || null);
  const openedAt = useRef(typeof performance !== 'undefined' ? performance.now() : 0);
  const lastReply = useRef(reply);
  const [reading, setReading] = useState(false);
  useEffect(() => {
    if (reply === lastReply.current) return undefined;
    lastReply.current = reply;
    if (!reply || performance.now() - openedAt.current < 1500) return undefined;
    const words = String(reply).trim().split(/\s+/).length;
    const ms = Math.min(SAY_MAX_S, Math.max(SAY_MIN_S, words * SAY_S_PER_WORD)) * 1000;
    setReading(true);
    const t = setTimeout(() => setReading(false), ms);
    return () => clearTimeout(t);
  }, [reply]);
  const on = !!id && (voice || reading);
  useEffect(() => {
    if (!id) return undefined;
    if (on) useSceneChat.setState({ speaking: id });
    else if (useSceneChat.getState().speaking === id) useSceneChat.setState({ speaking: null });
    return () => { if (useSceneChat.getState().speaking === id) useSceneChat.setState({ speaking: null }); };
  }, [id, on]);
}
